import dotProp from 'dot-prop-immutable';
import _ from 'lodash';
import {
  getParent,
  getAllChildItems,
} from '../services/product';
import {
  chooseMenu, updateMenu, clampManualQuantity, clearDistributeManualQuantities,
  getDistributeGroup, getDistributeGroupNaturalQuantity,
  getMenuId,
} from './actions/rules';
import {SUB_MENU, SUB_MENU_LIST} from "../constants/MenuItemTypes";

export const LOAD_QUOTE = 'LOAD_QUOTE';
export const SET_ACTIVE_MENU_ITEM = 'SET_ACTIVE_MENU_ITEM';
export const SET_EVENT_TYPE = 'SET_EVENT_TYPE';
export const SET_SELECTED_DATE = 'SET_SELECTED_DATE';
export const REQUEST_DATE_PRICE = 'REQUEST_DATE_PRICE';
export const LOAD_DATE_PRICE = 'LOAD_DATE_PRICE';
export const DATE_PRICE_ERROR = 'DATE_PRICE_ERROR';
export const SET_GUEST_AMOUNT = 'SET_GUEST_AMOUNT';
export const TOGGLE_PRODUCT = 'TOGGLE_PRODUCT';
export const SET_QUANTITY = 'SET_QUANTITY';
export const SET_URL_DATE = 'SET_URL_DATE';
export const SET_CATERING_MINIMUM_SPEND = 'SET_CATERING_MINIMUM_SPEND';

export const SET_GUESTS = 'SET_GUESTS';
export const OPEN_MENU = 'OPEN_MENU';
export const REMOVE_MENU = 'REMOVE_MENU';
export const SELECT_MENU = 'SELECT_MENU';
export const STASH_MENU = 'STASH_MENU';
export const UPDATE_MENU_ITEM = 'UPDATE_MENU_ITEM';
export const SELECT_PRODUCT = 'SELECT_PRODUCT'
export const UPDATE_CHOICES = 'UPDATE_CHOICES';
export const UPDATE_QUANTITIES = 'UPDATE_QUANTITIES';

export const HIGHLIGHT_SUBMENU_ITEMS = 'HIGHLIGHT_SUBMENU_ITEMS'
export const SELECT_COMPULSORY_ITEMS = 'SELECT_COMPULSORY_ITEMS'

// A manual pin is only valid for the guest count it was made against, so a
// guest-count change has to clear distribute-group pins in the STASHED snapshots
// too — not just the live menu. Otherwise switching back to a stashed menu
// restores a pin derived from a head count that no longer applies. Clones first:
// a restored menu shares item references with its snapshot (LOAD_QUOTE is handed
// stashedMenus[id] directly), so mutating in place would reach into the live quote.
const clearStashedDistributeManualQuantities = (stashedMenus) => (
  _.mapValues(stashedMenus, (stashedMenu) => (
    clearDistributeManualQuantities(_.cloneDeep(stashedMenu))
  ))
);

export function quoteReducer(
  state = {
    menu: {},
    eventType: '',
    activeMenuItem: '',
    selectedDate: {},
    guests: {},
    urlDate: {},
    cateringMinimumSpend: 0.0,
    minimum: 0.0,
    stashedMenus: {},
  },
  action,
) {
  switch (action.type) {
    case LOAD_QUOTE: {
      if (action.preserveSelections) {
        const receivedId = getMenuId(action.payload);
        const currentId = getMenuId(state.menu);
        // Resolve against the live state, not the snapshot captured when a
        // request started: customers may still be editing while it is in flight.
        if (receivedId && receivedId === currentId) {
          return state;
        }
        const stashedMenus = { ...state.stashedMenus };
        if (currentId) {
          stashedMenus[currentId] = _.cloneDeep(state.menu);
        }
        const menu = stashedMenus[receivedId]
          ? _.cloneDeep(stashedMenus[receivedId]) : action.payload;
        return { ...state, menu, stashedMenus };
      }
      return { ...state, menu: action.payload};
    }
    case SET_EVENT_TYPE: {
      return { ...state, eventType: action.payload };
    }
    case SET_GUESTS: {
      let menu = { ...state.menu };
      clearDistributeManualQuantities(menu);

      return {
        ...state,
        guests: action.payload,
        menu,
        stashedMenus: clearStashedDistributeManualQuantities(state.stashedMenus),
      };
    }
    case HIGHLIGHT_SUBMENU_ITEMS: {

        let menu = { ...state.menu };

        _.each(menu, (item) => {

          if([SUB_MENU, SUB_MENU_LIST].includes(item.menu_item_type_id)) {
            getAllChildItems(item, menu).forEach((i) => {
                menu[i.id].is_submenu = true;
            });
          }
        });
      return dotProp.set(state, 'menu', menu);
    }
    case SELECT_COMPULSORY_ITEMS: {
        let menu = { ...state.menu };
      _.each(menu, (item) => {
        if(item.is_compulsory === 1 && !item.is_submenu) {
          item.selected = true;
        }
      });

      return dotProp.set(state, 'menu', menu);
    }
    case UPDATE_QUANTITIES: {
      let menu = { ...state.menu };
      let guests = { ...state.guests };
      _.each(menu, (item) => {
        menu = updateMenu(item, menu, guests);
      });
      return dotProp.set(state, 'menu', menu);
    }
    case SET_SELECTED_DATE: {
      return {
        ...state,
        selectedDate: action.payload,
      };
    }
    case REQUEST_DATE_PRICE: {
      return { ...state, datePriceRequestId: action.payload.requestId,
        selectedDate: { date: action.payload.date, priceStatus: 'loading' } };
    }
    case LOAD_DATE_PRICE:
    case DATE_PRICE_ERROR: {
      if (action.payload.requestId !== state.datePriceRequestId
        || action.payload.eventTypeId !== state.eventType.id) return state;
      return { ...state, selectedDate: { date: state.selectedDate.date,
        ...(action.type === LOAD_DATE_PRICE
          ? { price: action.payload.price, priceStatus: 'ready' }
          : { priceStatus: 'error', priceError: action.payload.message }) } };
    }
    case UPDATE_MENU_ITEM: {
      const { item } = action.payload;
      return dotProp.set(state, `menu.${item.id}`, item);
    }
    case SET_QUANTITY: {
      const { item, quantity, manual } = action.payload;
      const current = state.menu[item.id];
      if (!current) {
        return state;
      }
      const clamped = clampManualQuantity(current, state.menu, state.guests, quantity);
      const isDistributeMember = getDistributeGroup(current, state.menu) !== null;
      // For a distribute-group member, comparing `clamped` against the item's
      // PREVIOUS quantity (as this used to for every item) is the wrong
      // baseline: a `-` click that gets clamped back up can land on a
      // different number than it started at while still being exactly what
      // the engine would have assigned anyway (e.g. pinned at 5, click `-`,
      // clamp forces it back to the group's natural 4) — the old check read
      // that as a genuine change and pinned it, desynchronising it from its
      // distribute-group siblings; a later sibling deselect then left the
      // group totalling 19 bottles instead of 20 (CR-186 review).
      //
      // The correct baseline for a distribute member is the engine's OWN
      // answer for this item right now: if the clamped value matches what
      // getDistributeGroupNaturalQuantity says the engine would assign, this
      // edit is indistinguishable from a no-op and the item goes back to
      // being owned by the engine (not pinned), so it keeps reflowing with
      // its group. Only a value that genuinely differs from the engine's own
      // answer is a real override.
      //
      // getDistributeGroupNaturalQuantity has no group redistribution to
      // compare against for a non-distribute item, so it falls back to the
      // item's own (pre-edit) quantity — degenerating this comparison into
      // "did the value change at all". Applying the same un-pin-on-match rule
      // there wrongly forces manually_set_quantity to false on any no-op
      // re-commit of an already-pinned value (e.g. focusing and blurring the
      // quantity box without changing it), silently dropping the customer's
      // pin. So a non-distribute item keeps the original unchanged-vs-previous
      // baseline instead: a no-op preserves whatever the marker already was,
      // and only a real change sets it from `manual`.
      const matchesNaturalShare = isDistributeMember && clamped === getDistributeGroupNaturalQuantity(
        current, state.menu, state.guests,
      );
      const unchanged = clamped === current.quantity;
      // Written as branches rather than nested ternaries so the two baselines
      // stay readable (and to keep the eslint warning backlog where it was).
      // `matchesNaturalShare` already carries the isDistributeMember gate.
      let manuallySetQuantity = manual === true;
      if (matchesNaturalShare) {
        // Back to the engine's own answer for this item: hand ownership back so
        // it keeps reflowing with its distribute group.
        manuallySetQuantity = false;
      } else if (!isDistributeMember && unchanged) {
        // A no-op re-commit must not drop an existing pin.
        manuallySetQuantity = current.manually_set_quantity === true;
      }
      const updated = {
        ...current,
        quantity: clamped,
        total: _.multiply(clamped, current.unit_price),
        manually_set_quantity: manuallySetQuantity,
      };

      return dotProp.set(state, `menu.${item.id}`, updated);
    }
    case SET_URL_DATE: {
      return {
        ...state,
        urlDate: action.payload,
        selectedDate: {date: action.payload},
      };
    }
    case SET_CATERING_MINIMUM_SPEND: {
      return {
        ...state,
        cateringMinimumSpend: action.payload.catering_minimum_spend,
        minimum: action.payload.minimum,
      }
    }
    case SELECT_MENU: {
      let menu = { ...state.menu };
      return dotProp.set(state, 'menu', menu);
    }
    case STASH_MENU: {
      // Snapshot a deep CLONE, not the live menu object: action.payload.currentMenu
      // shares item references with state.menu, so storing it directly would let a
      // later write to the snapshot leak into the live quote.
      //
      // Manual pins are deliberately KEPT in the snapshot. Stripping them here (as
      // this did) broke CR-186's own AC (f) — "a menu-load/stash-restore with no
      // guest-count change must NOT clear manual markers" — because pages/Quote.jsx
      // stashes, re-fetches and restores on every menu load, so a customer's typed
      // topping quantity was wiped by an ordinary re-fetch (CR-190). Staleness is
      // handled where it actually arises instead: a guest-count change clears pins
      // in the stashed snapshots as well as in the live menu, below.
      const stashedMenus = {
        ...state.stashedMenus,
        [action.payload.menuId]: _.cloneDeep(action.payload.currentMenu),
      };
      return {
        ...state,
        stashedMenus,
      };
    }
    case OPEN_MENU: {
      let menu = _.cloneDeep(state.menu);
      let guests = { ...state.guests };
      const { item } = action.payload;


      _.each(getAllChildItems(item, menu), (i) => {
        if(item.id !== i.id) {
          menu[i.id].submenu = item.id;
        }

      });

      menu = chooseMenu(item, menu, guests);

      return dotProp.set(state, 'menu', menu);
    }
    case REMOVE_MENU: {
      const menu = { ...state.menu };
      const { item } = action.payload;
      menu[item.id].selected = false;
      // Also clear a stale manual pin here (not via setItemUnSelected, which
      // would change other behaviour of this case that isn't in scope) — a
      // hidden item should not silently carry a manual quantity into whatever
      // comes back next time this menu is opened (CR-186 review).
      menu[item.id].manually_set_quantity = false;
      _.each(getAllChildItems(item, menu), (i) => {
        // Hide all items
        menu[i.id].selected = false;
        menu[i.id].manually_set_quantity = false;
      });
      const parent = getParent(item, menu);
      menu[parent.id].selected = false;
      return dotProp.set(state, 'menu', menu);
    }
    case SET_GUEST_AMOUNT: {
      let menu = { ...state.menu };
      clearDistributeManualQuantities(menu);

      const cleared = {
        ...state,
        stashedMenus: clearStashedDistributeManualQuantities(state.stashedMenus),
      };

      return dotProp.set(
        dotProp.set(cleared, 'menu', menu),
        `guests.${action.payload.id}.total`,
        parseInt(action.payload.value),
      );
    }
    case SELECT_PRODUCT: {
      let { item } = action.payload;
      return dotProp.set(state,`menu.${item.id}`, {...item, selected: !item.selected});
    }
    case UPDATE_CHOICES: {
      let { menu } = action.payload;
      return dotProp.set(state, 'menu', menu);
    }
    default:
      return state;
  }
}
